<template>
	<Transition
		enter-active-class="transition ease-out duration-500"
		enter-from-class="opacity-0 translate-y-4"
		enter-to-class="opacity-100 translate-y-0"
		leave-active-class="transition ease-in duration-200"
		leave-from-class="opacity-100 translate-y-0"
		leave-to-class="opacity-0 translate-y-4">
		<div
			v-if="show && post"
			class="fixed inset-x-4 bottom-4 z-40 ml-auto max-w-sm rounded-xl bg-white shadow-2xl ring-1 ring-gray-900/15 overflow-hidden sm:inset-x-auto sm:bottom-8 sm:right-8 sm:w-[32rem] sm:max-w-none">
			<button
				type="button"
				class="absolute top-2 right-2 rounded-full bg-white p-1.5 text-gray-400 shadow-sm hover:text-gray-600"
				@click="dismiss">
				<XMarkIcon class="h-5 w-5" />
			</button>
			<div class="flex flex-col sm:flex-row">
				<img
					v-if="post.imageUrl"
					:src="post.imageUrl"
					:alt="post.title"
					class="aspect-video w-full flex-shrink-0 object-cover sm:aspect-square sm:w-44">
				<div class="flex min-w-0 flex-1 flex-col justify-between p-6">
					<div class="pr-6">
						<h2 class="text-xs font-semibold uppercase tracking-wide text-indigo-500">
							Latest Blog Post
						</h2>

						<h3 class="mt-3 text-base font-bold leading-snug text-dbBlue">
							{{ post.title }}
						</h3>

						<p class="mt-3 line-clamp-3 text-sm text-gray-500">
							{{ post.description }}
						</p>
					</div>

					<div class="mt-5 flex items-center justify-between">
						<a
							:href="post.url"
							target="_blank"
							rel="noopener noreferrer"
							class="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-800"
							@click="dismiss">
							Read More
							<ArrowTopRightOnSquareIcon class="h-4 w-4 shrink-0" />
						</a>
					</div>
				</div>
			</div>
		</div>
	</Transition>
</template>

<script lang="ts">
	import {
		ref
	} from "vue"

	export default { name: "BlogPostPopup" } // name the component

	// module-scoped so a dismissal survives route navigation but resets on a full page reload
	const dismissed = ref(false)
</script>

<script lang="ts" setup>
	import {
		XMarkIcon
	} from "@heroicons/vue/20/solid"

	import {
		ArrowTopRightOnSquareIcon
	} from "@heroicons/vue/24/outline"

	import {
		onMounted
	} from "vue"

	import api from "@/utilities/api"

	const POPUP_DELAY_MS = 1500

	const show = ref(false)
	const post = ref<{title: string, description: string, imageUrl: string | null, date: string, url: string} | null>(null)

	const dateFormatter = new Intl.DateTimeFormat("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric"
	})

	onMounted(() => {
		if (!dismissed.value) {
			getLatestBlogPost()
		}
	})

	// hide the popup and remember the dismissal for the rest of this browser session
	function dismiss() {
		show.value = false
		dismissed.value = true
	}

	// resolves once the image is cached by the browser (or has failed), so it never flashes in on display
	function preloadImage(url: string) {
		return new Promise<void>((resolve) => {
			const img = new Image()
			img.onload = () => resolve()
			img.onerror = () => resolve()
			img.src = url
		})
	}

	// fetch the newest published blog entry relevant to the user's typeOfWork (falling back to the newest overall), then reveal the popup after a short delay
	async function getLatestBlogPost() {
		try {
			const response = await api("get", "fmDataAPI", "/latest-blog-post")

			if (!response.post) {
				return
			}

			post.value = {
				...response.post,
				date: dateFormatter.format(new Date(response.post.date))
			}

			const delay = new Promise((resolve) => setTimeout(resolve, POPUP_DELAY_MS))
			const imageReady = post.value.imageUrl ? preloadImage(post.value.imageUrl) : Promise.resolve()

			await Promise.all([delay, imageReady])

			if (!dismissed.value) {
				show.value = true
			}
		}
		catch(e) {
			console.error(e)
		}
	}
</script>
